<script setup lang="ts">
const { close, isOpen } = useAboutModal()
const { trackEvent } = useAnalytics()

const { data: about } = await useAsyncData('component-about', () =>
  queryCollection('componentAbout').first(),
)

// Footer note uses the same {kaweahTech} placeholder pattern as the
// site footer so the "Kaweah Tech" portion of the line is rendered as
// a tracked outbound link.
const footerNoteParts = computed(() => {
  const line = about.value?.footerNote ?? ''
  const [ before, after = '' ] = line.split('{kaweahTech}')
  return { after, before }
})

function handleContactClick() {
  trackEvent('about_modal_cta_clicked', { destination: 'contact' })
}

function handleKaweahTechClick() {
  trackEvent('external_link_clicked', {
    destination: 'kaweah_tech',
    source: 'about_modal',
  })
}
</script>

<template>
  <UModal
    v-model:open="isOpen"
    :ui="{
      overlay: 'sfg-about-overlay',
      content: 'rotate-[-0.6deg]',
    }"
  >
    <template #content>
      <div class="bg-brand-ink text-brand-chalk rounded-card overflow-hidden">
        <div v-if="about" class="flex flex-col items-center gap-6 p-8 sm:p-10 text-center">
          <SfgFlame
            :size="96"
            :disk="false"
            body-color="red"
            flame-color="yellow"
            :tilt="2"
          />

          <h2 class="font-editorial italic font-black text-[32px] leading-none">
            {{ about.heading }}
          </h2>

          <div class="flex flex-col gap-4 max-w-md">
            <p
              v-for="(paragraph, i) in about.body"
              :key="i"
              class="font-body text-body leading-relaxed"
            >
              {{ paragraph }}
            </p>
          </div>

          <!-- Primary CTA: opens contact in a new tab so an active game
               session on this page isn't disturbed. -->
          <SfgButton
            :to="about.contactCta.to"
            target="_blank"
            rel="noopener noreferrer"
            variant="primary"
            size="sm"
            @click="handleContactClick"
          >
            {{ about.contactCta.label }}
          </SfgButton>

          <p class="font-display uppercase text-[10px] tracking-[0.18em] opacity-60">
            {{ footerNoteParts.before }}<a
              :href="about.kaweahTechUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="text-inherit underline decoration-dotted underline-offset-2 hover:opacity-100"
              @click="handleKaweahTechClick"
            >Kaweah Tech</a>{{ footerNoteParts.after }}
          </p>

          <SfgButton
            variant="ghost"
            size="sm"
            @click="close"
          >
            Close
          </SfgButton>
        </div>
      </div>
    </template>
  </UModal>
</template>
